import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CopyProvider } from "@/contexts/CopyContext";
import { AdminProvider } from "@/contexts/AdminContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import { LiveProvider } from "@/contexts/LiveContext";
import ThemeFontController from "@/components/ThemeFontController";
import AdminPinPad from "@/components/AdminPinPad";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Dashboard from "@/pages/Dashboard";
import Groups from "@/pages/Groups";
import EventDetail from "@/pages/EventDetail";
import FundraiserSuccess from "@/pages/FundraiserSuccess";
import AuthCallback from "@/pages/AuthCallback";
import Join from "@/pages/Join";
import Admin from "@/pages/Admin";
import AdminBranding from "@/pages/AdminBranding";
import AdminAnalytics from "@/pages/AdminAnalytics";
import AdminDigest from "@/pages/AdminDigest";
import Profile from "@/pages/Profile";
import Invites from "@/pages/Invites";
import InvitePreview from "@/pages/InvitePreview";
import { Loader2 } from "lucide-react";

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === null) {
    return (
      <div className="min-h-screen grid place-items-center" style={{ background: "var(--bg)", color: "var(--text)" }}>
        <Loader2 className="animate-spin" />
      </div>
    );
  }
  if (!user || !user.user_id) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/invite/:token" element={<InvitePreview />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/app" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/app/join" element={<ProtectedRoute><Join /></ProtectedRoute>} />
      <Route path="/groups" element={<ProtectedRoute><Groups /></ProtectedRoute>} />
      <Route path="/events/:eventId" element={<ProtectedRoute><EventDetail /></ProtectedRoute>} />
      <Route path="/fundraiser/success" element={<ProtectedRoute><FundraiserSuccess /></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>} />
      <Route path="/admin/branding" element={<ProtectedRoute><AdminBranding /></ProtectedRoute>} />
      <Route path="/admin/analytics" element={<ProtectedRoute><AdminAnalytics /></ProtectedRoute>} />
      <Route path="/admin/digest" element={<ProtectedRoute><AdminDigest /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/invites" element={<ProtectedRoute><Invites /></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AdminProvider>
          <BrandingProvider>
            <NotificationProvider>
              <LiveProvider>
                <CopyProvider>
                  <BrowserRouter>
                    <AppRouter />
                    <ThemeFontController />
                    <AdminPinPad />
                  </BrowserRouter>
                </CopyProvider>
              </LiveProvider>
            </NotificationProvider>
          </BrandingProvider>
        </AdminProvider>
      </AuthProvider>
    </ThemeProvider>
  );
}
